/* eslint-disable react/no-unknown-property */
import { Button, Icon, Tab, Tooltip } from '@pypestream/design-system';
import { observer } from 'mobx-react-lite';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  describeNoTestChannel,
  listTestChannels,
  listVoiceTestChannels,
  selectTestChannel,
} from '../../components/test-surface';
import { tableStore } from '../../stores/table-store';
import { isGroupRow } from '../../stores/utils/group-row-guards';
import { nodeKey } from '../../stores/utils/solution-health';
import { nodeNumberToGoTo } from '../../stores/utils/node-number';
import { getChannelsFromPypes } from '../../utils';
import {
  criterionHelp,
  passAt,
  PASS_AT,
  RESPONSE_TARGET_S,
  setPassAt,
  qualityScore,
  scoreBreakdown,
  verdictCriteria,
} from '../../utils/evaluation-rubric';
import { setValidateProject } from './validate-project';
import { useProStudioCtxSelector } from '../../xstate/app.xstate';
import { buildCanvasAgentNodes } from '../canvas-view/canvas-view-model';
import {
  LiveChat,
  type LiveChatState,
  type LiveEnded,
  type LiveReply,
} from './live-chat';
import { agentsReached, routingVerdict } from './engine-trace-match';
import { runScenario, type ScenarioStep } from './scenario-run';
import { casesFromCsv, suiteFromJson } from './suite-io';
import { runOfSuiteTask, scenarioInputOf, withJudge } from './suite-task';
import { useSharedHistory } from './use-shared-history';
import { SchedulePanel } from './validate-schedule-view';
import { agentChoices, useExpectedAgentEnabled } from './validate-agents';
import { scoreFocus } from './turn-scoring';
import {
  Bubbles,
  ScenarioPanel,
  ScoringPanel,
  TestPanel,
  type WebviewHandlers,
} from './validate-panels';
import { useEngineTrace } from './use-engine-trace';
import { localValidateStore } from './validate-store';
import { withoutIcons } from './icon-tokens';
import {
  asRubricCriteria,
  rubricConfig,
  rubricCriteriaOf,
} from '../../utils/judges';
import {
  savedJudge,
  scoringJudgeOf,
  type RunJudgeEntry,
  type TeamSample,
  type ValidateJudge,
} from './validate-judges';
import {
  JudgeDetail,
  JudgesView,
  NewJudgePanel,
  RunJudges,
} from './validate-judges-view';
import {
  caseFromRun,
  duplicateSuite,
  expandSuite,
  latestSuiteRuns,
  newSuite,
  runWithConcurrency,
  type SuiteRun,
  type ValidateSuite,
} from './validate-suites';
import {
  SaveCasePanel,
  SuitePanel,
  SuitesView,
  type SuiteProgress,
} from './validate-suites-view';
import {
  TIME_FILTERS,
  filterByKind,
  filterByTime,
  absoluteTime,
  kindCounts,
  latestUserText,
  runPassed,
  runQuality,
  runResponseMs,
  summarizeRuns,
  turnTally,
  type KindFilter,
  channelKey,
  type RunChannel,
  type RunScore,
  type ExpectedAgent,
  type StartPosition,
  type TimeFilter,
  type ValidateRun,
  type ValidateTurn,
} from './validate-runs';

import { buildVoiceLines } from './validate-voice-lines';
import './validate-stage.scss';
import { BobcatTabGroup } from '../../components/bobcat-tab-group';

/**
 * Validate — the stage's content with `validateStage` on.
 *
 * Built from the testing tool's pieces (`wbd-langfuse-demo-frontend/apps/tester`):
 * the live Test chat is its Engagement API runner (via `@pypestream/testview`),
 * New scenario is its conversation planner, every reply is scored by its
 * evaluator (on the evaluation rubric, through `/ai/validate/*`), and a run
 * links to its trace in the Observer the same way the tester's Tests view
 * does. Layout is the prototype's (`prostudio-build-flow-layouts.html`).
 */

type Panel =
  | 'test'
  | 'scoring'
  | 'scenario'
  | 'suite'
  | 'save-case'
  | 'judge-new'
  | 'schedule';
type View = 'runs' | 'suites' | 'judges';

const store = localValidateStore;

/**
 * A list kept per solution in the Validate store. Skips the render that
 * loaded it, so a solution switch can't write the previous solution's list
 * under the new key.
 */
const usePersistedList = <T,>(
  solutionKey: string | null,
  load: (key: string) => T[],
  save: (key: string, list: T[]) => void
) => {
  const [list, setList] = useState<T[]>([]);
  useEffect(() => {
    setList(solutionKey ? load(solutionKey) : []);
    // `load` is a stable store method.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [solutionKey]);
  const loadedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!solutionKey) return;
    if (loadedFor.current !== solutionKey) {
      loadedFor.current = solutionKey;
      return;
    }
    save(solutionKey, list);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list, solutionKey]);
  return [list, setList] as const;
};

const newId = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `run-${Date.now()}-${Math.random().toString(36).slice(2)}`;

const EMPTY_CHAT: LiveChatState = {
  status: 'idle',
  transcript: [],
  chatId: null,
  error: null,
};

/** A Test chat's run id: one run per conversation, found again by chat. */
const chatRunId = (chatId: string) => `chat-${chatId}`;

/* ---- Summary ------------------------------------------------------------ */

const Tip: FC<{ text: string }> = ({ text }) => (
  <Tooltip content={text} placement="top">
    <span className="c-validate-tip" aria-label={text}>
      <Icon name="info" size="xsmall" />
    </span>
  </Tooltip>
);

const Summary: FC<{ runs: ValidateRun[] }> = ({ runs }) => {
  const s = summarizeRuns(runs);
  const passShare = s.scored ? (s.passed / s.scored) * 100 : 0;
  const avgWidth =
    s.avgResponseS === null
      ? 0
      : Math.min(100, (s.avgResponseS / (RESPONSE_TARGET_S * 2)) * 100);

  return (
    <div className="c-validate-summary" test-id="bobcat-validate-summary">
      <div className="c-validate-tile c-validate-tile--big">
        <span className="c-validate-tile__label">
          Success rate
          <Tip
            text={`A run passes when its quality score is ${passAt()}/100 or higher and nothing auto-fails. Success rate is the share of scored runs in view that pass.`}
          />
        </span>
        <div className="c-validate-tile__row">
          <span className="c-validate-tile__hero">
            {s.successRate ?? '—'}
            {s.successRate !== null && <small>%</small>}
          </span>
          <div className="c-validate-tile__detail">
            <b>
              {s.scored
                ? `${s.passed} of ${s.scored} runs passed`
                : 'No scored runs yet'}
            </b>
            <span>
              A run passes at {passAt()}/100 on the quality rubric, with nothing
              auto-failing
            </span>
            <div className="c-validate-bar" aria-hidden="true">
              <i className="is-pass" style={{ width: `${passShare}%` }} />
              <i
                className="is-fail"
                style={{ width: `${s.scored ? 100 - passShare : 0}%` }}
              />
            </div>
            <span className="c-validate-legend">
              <i className="is-pass" /> Passed {s.passed}
              <i className="is-fail" /> Failed {s.failed}
            </span>
          </div>
        </div>
      </div>

      <div className="c-validate-tile">
        <span className="c-validate-tile__label">
          Response time
          <Tip
            text={`From the customer's message to the first frame of the reply, measured in this browser. Target ${RESPONSE_TARGET_S}s.`}
          />
        </span>
        <span className="c-validate-tile__value">
          {s.avgResponseS === null ? '—' : s.avgResponseS.toFixed(2)}
          <small>s avg</small>
        </span>
        <div className="c-validate-meter" aria-hidden="true">
          <i style={{ width: `${avgWidth}%` }} />
          <b style={{ left: '50%' }} />
        </div>
        <span className="c-validate-tile__sub">
          {s.p95ResponseS === null
            ? 'Nothing timed yet'
            : `p95 ${s.p95ResponseS.toFixed(2)}s · ${s.underTarget} of ${s.timed} under the ${RESPONSE_TARGET_S.toFixed(1)}s target`}
        </span>
      </div>

      <div className="c-validate-tile">
        <span className="c-validate-tile__label">
          Quality score
          <Tip text="Each reply is scored 1–5 on the Quality score judge's criteria (as standard: tone, clear communication, accuracy, relevance and resolution, 20 points each), summed out of 100; any auto-fail condition scores 0." />
        </span>
        <span className="c-validate-tile__value">
          {s.qualityAvg ?? '—'}
          <small>/ 100 avg</small>
        </span>
        <span className="c-validate-tile__sub">
          {s.criterionAvgs
            ? s.criteriaSeen
                .map(
                  (c) =>
                    `${c.short} ${(s.criterionAvgs![c.key] ?? 0).toFixed(1)}`
                )
                .join(' · ')
            : 'Scores appear as runs are judged'}
        </span>
      </div>
    </div>
  );
};

/* ---- Runs --------------------------------------------------------------- */

/** "3/4 turns pass", beside the conversation's own score. */
const TurnTallyChip: FC<{ run: ValidateRun }> = ({ run }) => {
  const t = turnTally(run);
  if (!t.scored && !t.pending) return null;
  return (
    <span
      className={`c-validate-run__turns${t.failed ? ' has-fail' : ''}`}
      test-id="bobcat-validate-run-turns"
      title={
        t.pending
          ? `${t.pending} ${t.pending === 1 ? 'reply is' : 'replies are'} still being scored`
          : 'Replies that passed on their own'
      }
    >
      {t.passed}/{t.scored + t.pending} turns
    </span>
  );
};

const ScoreChip: FC<{ run: ValidateRun }> = ({ run }) => {
  if (run.score.status === 'idle') {
    return (
      <Tooltip
        content="The conversation hasn’t been scored yet — it is when the chat ends."
        placement="top"
      >
        <span
          className="c-validate-chip is-idle"
          test-id="bobcat-validate-run-score"
        >
          …
        </span>
      </Tooltip>
    );
  }
  if (run.score.status === 'pending') {
    return (
      <span
        className="c-validate-chip is-pending"
        test-id="bobcat-validate-run-score"
      >
        Scoring…
      </span>
    );
  }
  if (run.score.status === 'error') {
    return (
      <Tooltip content={run.score.message} placement="top">
        <span
          className="c-validate-chip is-error"
          test-id="bobcat-validate-run-score"
        >
          —
        </span>
      </Tooltip>
    );
  }
  const passed = runPassed(run);
  return (
    <Tooltip content={scoreBreakdown(run.score.verdict)} placement="top">
      <span
        className={`c-validate-chip ${passed ? 'is-pass' : 'is-fail'}`}
        test-id="bobcat-validate-run-score"
      >
        {runQuality(run)}
      </span>
    </Tooltip>
  );
};

const Meter: FC<{ value: number }> = ({ value }) => (
  <span className="c-validate-pips" aria-label={`${value} of 5`}>
    {[1, 2, 3, 4, 5].map((n) => (
      <i key={n} className={n <= value ? 'on' : ''} />
    ))}
  </span>
);

/**
 * Routing, measured: which agents the chat's engine trace walked through,
 * against the one the run expected. A fact off the trace — no model.
 */
const useRoutingVerdict = (run: ValidateRun | null) => {
  const trace = useEngineTrace(run?.chatId ?? null, run?.transcript ?? []);
  const rows = tableStore.getAllSortedRows();
  if (!run?.expectedAgent) return { status: 'unknown' as const };
  const nodeAgent = new Map<string, string>();
  rows.forEach((r) => {
    const groupId = r.settings?.groupId;
    if (groupId && !isGroupRow(r))
      nodeAgent.set(nodeKey(r.nodeNumber), groupId);
  });
  const agents = buildCanvasAgentNodes(rows);
  const core = new Set(
    agents.filter((a) => a.category === 'core').map((a) => a.id)
  );
  const verdict = routingVerdict({
    expectedId: run.expectedAgent.id,
    reached: agentsReached(trace.requests, nodeAgent),
    coreIds: core,
    hasTrace: trace.requests.length > 0,
  });
  return verdict.status === 'fail'
    ? {
        ...verdict,
        reachedTitle: verdict.reachedId
          ? agents.find((a) => a.id === verdict.reachedId)?.title
          : undefined,
      }
    : verdict;
};

const RunDetail: FC<{
  run: ValidateRun;
  onSaveCase?: (run: ValidateRun) => void;
  onScoreConversation?: (run: ValidateRun) => void;
  judges?: ValidateJudge[];
  onJudgeResult?: (
    runId: string,
    judgeId: string,
    entry: RunJudgeEntry
  ) => void;
}> = ({ run, onSaveCase, onScoreConversation, judges, onJudgeResult }) => {
  const expectedAgentEnabled = useExpectedAgentEnabled();
  const routing = useRoutingVerdict(
    expectedAgentEnabled && run.expectedAgent ? run : null
  );
  const tally = turnTally(run);
  const ms = runResponseMs(run);
  // A Test run shows the exchange it scored; the turns before it were context.
  const shown: ValidateTurn[] =
    run.focusIndex === null
      ? run.transcript
      : run.transcript.slice(
          Math.max(
            0,
            run.transcript
              .slice(0, run.focusIndex)
              .map((t) => t.role)
              .lastIndexOf('user')
          ),
          run.focusIndex + 1
        );

  return (
    <div className="c-validate-run__body">
      <div className="c-validate-run__convo">
        <div className="c-validate-run__on">
          <Icon name="chat" size="xsmall" /> On {run.channel.name}
          {run.versionLabel ? ` · ${run.versionLabel}` : ''}
          {run.suite && (
            <span
              className="c-validate-run__suite"
              test-id="bobcat-validate-run-suite"
            >
              {' '}
              · Suite <b>{run.suite.suiteName}</b> · {run.suite.caseName}
              {run.suite.iteration > 1 ? ` (run ${run.suite.iteration})` : ''}
            </span>
          )}
          {run.model && (
            <span test-id="bobcat-validate-run-model">
              {' '}
              · Model <b>{run.model}</b>
            </span>
          )}
        </div>
        {run.expectation && (
          <p className="c-validate-run__expect">
            <b>Expected:</b> {run.expectation}
          </p>
        )}
        <Bubbles
          transcript={shown}
          botName={run.channel.name}
          turnScores={run.turnScores}
          chatId={run.chatId}
          timeline
        />
        {run.stoppedBecause && (
          <p className="c-validate-note" test-id="bobcat-validate-run-stopped">
            The conversation stopped early: {run.stoppedBecause}
          </p>
        )}
      </div>
      <div className="c-validate-run__checks">
        {!!run.checks?.length && (
          <div
            className="c-validate-check"
            test-id="bobcat-validate-run-pattern-checks"
          >
            <span className="c-validate-check__label">Checks</span>
            {run.checks.map((c) => (
              <span
                key={c.label}
                className={`c-validate-routing c-validate-routing--${c.passed ? 'pass' : 'fail'}`}
              >
                {c.passed ? '✓' : '✗'} {c.label}
              </span>
            ))}
          </div>
        )}
        <div className="c-validate-check">
          <span className="c-validate-check__label">Response time</span>
          <span className="c-validate-check__value">
            {ms === null ? '—' : (ms / 1000).toFixed(2)}
            {ms !== null && <small>s</small>}
            {ms !== null && (
              <i
                className={ms / 1000 <= RESPONSE_TARGET_S ? 'is-ok' : 'is-slow'}
              />
            )}
          </span>
        </div>
        {expectedAgentEnabled && run.expectedAgent && (
          <div
            className="c-validate-check"
            test-id="bobcat-validate-run-routing"
          >
            <span className="c-validate-check__label">Routing</span>
            <span className="c-validate-check__line">
              Expected: <b>{run.expectedAgent.title}</b>
            </span>
            {routing.status === 'pass' && (
              <span className="c-validate-routing c-validate-routing--pass">
                ✓ Reached it
              </span>
            )}
            {routing.status === 'fail' && (
              <span className="c-validate-routing c-validate-routing--fail">
                ✗{' '}
                {routing.reachedTitle
                  ? `Went to ${routing.reachedTitle} instead`
                  : 'Never reached a use-case agent'}
              </span>
            )}
            {routing.status === 'unknown' && (
              <span className="c-validate-check__muted">
                Waiting for the engine trace…
              </span>
            )}
          </div>
        )}
        {(tally.scored > 0 || tally.pending > 0) && (
          <div
            className="c-validate-check"
            test-id="bobcat-validate-run-turn-summary"
          >
            <span className="c-validate-check__label">Replies</span>
            <span className="c-validate-check__line">
              <b>{tally.passed}</b> of {tally.scored + tally.pending} passed on
              their own
              {tally.pending ? ` · ${tally.pending} still scoring` : ''}
            </span>
            <span className="c-validate-check__muted">
              Each reply’s score is under it in the conversation.
            </span>
          </div>
        )}
        {run.score.status !== 'scored' && run.focusIndex === null && (
          <div
            className="c-validate-check"
            test-id="bobcat-validate-run-conversation"
          >
            <span className="c-validate-check__label">Conversation</span>
            <span className="c-validate-check__muted">
              {run.score.status === 'pending'
                ? 'Scoring the whole conversation…'
                : run.score.status === 'error'
                  ? run.score.message
                  : 'Not scored yet — Test chats are scored as a whole when they end.'}
            </span>
            {run.score.status !== 'pending' && onScoreConversation && (
              <button
                type="button"
                className="c-validate-link"
                test-id="bobcat-validate-run-score-conversation"
                onClick={() => onScoreConversation(run)}
              >
                {run.score.status === 'error'
                  ? 'Try again'
                  : 'Score conversation'}
              </button>
            )}
          </div>
        )}
        {run.score.status === 'scored' && (
          <div className="c-validate-check">
            <span className="c-validate-check__label c-validate-check__label--with-action">
              Conversation · {qualityScore(run.score.verdict)}/100
              {onScoreConversation && (
                <button
                  type="button"
                  className="c-validate-link c-validate-check__action"
                  test-id="bobcat-validate-run-rescore"
                  onClick={() => onScoreConversation(run)}
                >
                  Re-score
                </button>
              )}
            </span>
            {run.score.verdict.autoFail ? (
              <p className="c-validate-error">
                Auto-fail: {run.score.verdict.autoFail}
              </p>
            ) : (
              <div className="c-validate-rubric">
                {verdictCriteria(run.score.verdict).map((c) => {
                  const score = run.score as Extract<
                    RunScore,
                    { status: 'scored' }
                  >;
                  return (
                    <Tooltip
                      key={c.key}
                      content={score.reasons[c.key] ?? criterionHelp(c)}
                      placement="left"
                    >
                      <div className="c-validate-rubric__row">
                        <span>{c.short}</span>
                        <Meter value={score.verdict.scores[c.key]} />
                        <b>{score.verdict.scores[c.key]}</b>
                      </div>
                    </Tooltip>
                  );
                })}
              </div>
            )}
            {run.score.summary && (
              <p className="c-validate-run__summary">{run.score.summary}</p>
            )}
          </div>
        )}
        {run.score.status === 'scored' &&
          runPassed(run) === false &&
          run.score.whyFailed && (
            <div
              className="c-validate-why"
              test-id="bobcat-validate-run-why-failed"
            >
              <Icon name="warning" size="xsmall" />
              <span>
                <b>Why it failed.</b> {run.score.whyFailed}
              </span>
            </div>
          )}
        {run.score.status === 'scored' &&
          run.score.toneNote &&
          run.score.verdict.scores.tone <= 3 && (
            <div
              className="c-validate-why c-validate-why--tone"
              test-id="bobcat-validate-run-tone"
            >
              <Icon name="face-smile" size="xsmall" />
              <span>
                <b>Tone.</b> {run.score.toneNote}
              </span>
            </div>
          )}
        {run.score.status === 'error' && (
          <p className="c-validate-error">{run.score.message}</p>
        )}
        {judges && onJudgeResult && (
          <RunJudges run={run} judges={judges} onResult={onJudgeResult} />
        )}
      </div>
      <div className="c-validate-run__foot">
        <span className="c-validate-run__chat-id">Chat {run.chatId}</span>
        {run.kind === 'scenario' && onSaveCase && (
          <button
            type="button"
            className="c-validate-link"
            test-id="bobcat-validate-save-as-case"
            onClick={() => onSaveCase(run)}
          >
            <Icon name="add" size="xsmall" />
            Save as suite case
          </button>
        )}
      </div>
    </div>
  );
};

const RunRow: FC<{
  run: ValidateRun;
  open: boolean;
  onToggle: () => void;
  now: number;
  onSaveCase: (run: ValidateRun) => void;
  onScoreConversation: (run: ValidateRun) => void;
  judges: ValidateJudge[];
  onJudgeResult: (runId: string, judgeId: string, entry: RunJudgeEntry) => void;
  /** Someone else made it (shared history); null when it's yours. */
  author?: string | null;
}> = ({
  run,
  open,
  onToggle,
  now,
  onSaveCase,
  onScoreConversation,
  judges,
  onJudgeResult,
  author = null,
}) => (
  <div
    id={`validate-run-${run.id}`}
    className={`c-validate-run c-validate-run--${run.kind} ${open ? 'is-open' : ''}`}
    test-id="bobcat-validate-run"
  >
    <button type="button" className="c-validate-run__head" onClick={onToggle}>
      <span
        className="c-validate-run__when"
        title={new Date(run.startedAt).toLocaleString()}
      >
        {absoluteTime(run.startedAt, now)}
      </span>
      <span
        className={`c-validate-run__kind c-validate-run__kind--${run.kind}`}
        title={
          run.kind === 'scenario'
            ? `Scenario · starts at the ${run.startAt === 'mid' ? 'middle' : (run.startAt ?? 'beginning')}`
            : 'Test'
        }
      >
        <Icon name={run.kind === 'test' ? 'chat' : 'stars'} size="xsmall" />
      </span>
      <span className="c-validate-run__title">
        {run.kind === 'test'
          ? `“${withoutIcons(run.title)}”`
          : withoutIcons(run.title)}
      </span>
      {(run.suite?.scheduleId || author) && (
        <span className="c-validate-run__who" test-id="bobcat-validate-run-who">
          {run.suite?.scheduleId ? 'Scheduled' : 'Teammate'}
        </span>
      )}
      {run.chatId && (
        <span
          className="c-validate-run__chat"
          title={`Chat ${run.chatId}`}
          test-id="bobcat-validate-run-chat-id"
        >
          {run.chatId.slice(0, 8)}
        </span>
      )}
      <span className="c-validate-run__channel">
        {run.channel.env === 'live' ? 'Live' : 'Sandbox'}
      </span>
      <TurnTallyChip run={run} />
      <ScoreChip run={run} />
      <Icon name={open ? 'chevron-up' : 'chevron-down'} size="xsmall" />
    </button>
    {open && (
      <RunDetail
        run={run}
        onSaveCase={onSaveCase}
        onScoreConversation={onScoreConversation}
        judges={judges}
        onJudgeResult={onJudgeResult}
      />
    )}
  </div>
);

/* ---- Stage -------------------------------------------------------------- */

export const ValidateStage: FC = observer(() => {
  const { botPypes, deployedVersions, solutionKey, projectId } =
    useProStudioCtxSelector((ctx) => ({
      projectId: ctx.project?.id,
      botPypes: ctx.botPypes,
      deployedVersions: ctx.deployedVersions,
      solutionKey:
        ctx.customerId && ctx.botName
          ? `${ctx.customerId}.${ctx.botName}`
          : null,
    }));
  const pypeNextEnabled = useProStudioCtxSelector(
    (ctx) => ctx.featureFlags?.['pype-next'] ?? false
  );
  // Traces and call transcripts are read for this project's chats only.
  useEffect(() => {
    setValidateProject(projectId);
  }, [projectId]);

  const rawChannels = useMemo(
    () => (botPypes ? getChannelsFromPypes(botPypes) : null),
    [botPypes]
  );
  const channels: RunChannel[] = useMemo(
    () =>
      listTestChannels(rawChannels).map((c) => ({
        appId: c.appId,
        name: c.name,
        env: c.channelEnv,
      })),
    [rawChannels]
  );
  // Test also offers each voice channel, spoken in the browser through the
  // widget beside it. Suites and scenarios keep the widget channels.
  const testChannels: RunChannel[] = useMemo(
    () =>
      channels.concat(
        listVoiceTestChannels(rawChannels).map((c) => ({
          appId: c.appId,
          name: c.name,
          env: c.channelEnv,
          voiceChannelId: c.voiceChannelId,
        }))
      ),
    [channels, rawChannels]
  );
  const voiceLines = useMemo(() => buildVoiceLines(rawChannels), [rawChannels]);
  // Each node's message, for the bot's side of a phone call's transcript.
  const nodeMessages = useMemo(() => {
    const out = new Map<number, string>();
    tableStore.getAllSortedRows().forEach((r) => {
      const n = nodeNumberToGoTo(r.nodeNumber);
      if (n !== undefined && typeof r.message === 'string' && r.message) {
        out.set(Number(n), r.message);
      }
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableStore.rows]);
  const defaultTest = selectTestChannel(rawChannels);
  const defaultChannel =
    channels.find((c) => c.appId === defaultTest?.appId) ?? null;

  /* Agents — the Build groups, for "What should handle it?". */
  const rows = tableStore.getAllSortedRows();
  const agents = useMemo(
    () => agentChoices(buildCanvasAgentNodes(rows)),
    [rows]
  );

  const versionLabelFor = useCallback(
    (env: 'live' | 'sandbox') => {
      const v = deployedVersions?.[env];
      // deployedVersions may already carry the "v" ("v1"); never double it.
      return v ? `v${String(v).replace(/^v/i, '')}` : null;
    },
    [deployedVersions]
  );

  /* Runs, suites and suite runs — this browser's, per solution. */
  const [runs, setRuns] = usePersistedList<ValidateRun>(
    solutionKey,
    store.loadRuns,
    store.saveRuns
  );
  const [suites, setSuites] = usePersistedList<ValidateSuite>(
    solutionKey,
    store.loadSuites,
    store.saveSuites
  );
  const [suiteRuns, setSuiteRuns] = usePersistedList<SuiteRun>(
    solutionKey,
    store.loadSuiteRuns,
    store.saveSuiteRuns
  );

  const [judges, setJudges] = usePersistedList<ValidateJudge>(
    solutionKey,
    store.loadJudges,
    store.saveJudges
  );

  // Shared with the team when the solution's store is set up.
  const shared = useSharedHistory({
    projectId,
    solutionKey,
    runs,
    setRuns,
    suites,
    setSuites,
    suiteRuns,
    setSuiteRuns,
    judges,
    setJudges,
  });
  const [who, setWho] = useState<'mine' | 'everyone'>('everyone');
  const [schedulingSuite, setSchedulingSuite] = useState<string | null>(null);

  // The Quality score judge sets the pass bar. Set while rendering, so this
  // render's passes and fails use it.
  setPassAt(
    judges.find((j) => j.config.kind === 'rubric')?.config.bar ?? PASS_AT
  );
  // And it is what every score request carries — handed to each request by
  // its caller (`requestScore`'s `judge`), never kept where a later render
  // could change it under a request already running. The ref is for the
  // callbacks that must not change identity when a judge is edited
  // (`onReply` is a dependency of the Test chat's creation effect): they read
  // it when a request is issued, a scenario or suite once when it starts.
  // Synced in an effect, not while rendering.
  const scoringJudge = useMemo(() => scoringJudgeOf(judges), [judges]);
  const scoringJudgeRef = useRef(scoringJudge);
  useEffect(() => {
    scoringJudgeRef.current = scoringJudge;
  }, [scoringJudge]);
  const rubricJudge =
    judges.find((j) => j.config.kind === 'rubric')?.config ?? rubricConfig();
  const [openJudge, setOpenJudge] = useState<string | null>(null);
  const saveJudge = (judge: ValidateJudge) =>
    setJudges((prev) =>
      prev.map((j) => (j.id === judge.id ? savedJudge(judge, Date.now()) : j))
    );
  const setJudgeSamples = (id: string, samples: TeamSample[]) =>
    setJudges((prev) => prev.map((j) => (j.id === id ? { ...j, samples } : j)));
  const setRunJudgeResult = useCallback(
    (runId: string, judgeId: string, entry: RunJudgeEntry) =>
      setRuns((prev) =>
        prev.map((r) =>
          r.id === runId
            ? { ...r, judgeResults: { ...r.judgeResults, [judgeId]: entry } }
            : r
        )
      ),
    [setRuns]
  );

  const addRun = useCallback(
    (run: ValidateRun) => {
      setRuns((prev) => [run, ...prev]);
    },
    [setRuns]
  );
  const settleRun = useCallback(
    (id: string, score: RunScore) => {
      setRuns((prev) => prev.map((r) => (r.id === id ? { ...r, score } : r)));
    },
    [setRuns]
  );

  /* Test chat. */
  const [panel, setPanel] = useState<Panel>('test');
  const [chosenAppId, setChosenAppId] = useState<string | null>(null);
  const testChannel =
    testChannels.find((c) => channelKey(c) === chosenAppId) ?? defaultChannel;
  const [chat, setChat] = useState<LiveChatState>(EMPTY_CHAT);
  const liveChat = useRef<LiveChat | null>(null);
  // Stable, so a webview's message listener isn't re-bound every render.
  const webviewHandlers = useMemo<WebviewHandlers>(
    () => ({
      onData: (data, form) => liveChat.current?.sendWebviewData(data, form),
      onClose: (message) => void liveChat.current?.closeWebview(message),
    }),
    []
  );

  /** One reply's score, written into its run without touching the others. */
  const settleTurn = useCallback(
    (runId: string, index: number, score: RunScore) => {
      setRuns((prev) =>
        prev.map((r) =>
          r.id === runId
            ? {
                ...r,
                turnScores: { ...(r.turnScores ?? {}), [String(index)]: score },
              }
            : r
        )
      );
    },
    [setRuns]
  );

  /**
   * Score a whole conversation — when a Test chat ends, and on request (a
   * run's "Score conversation", the test panel's "Score now"/"Re-score").
   * Reads the run's own transcript, so it scores what was recorded.
   */
  const scoreConversation = useCallback(
    (run: ValidateRun) => {
      settleRun(run.id, { status: 'pending' });
      void scoreFocus({
        transcript: run.transcript,
        focusIndex: null,
        judge: scoringJudgeRef.current,
        ...(run.kind === 'scenario'
          ? { scenario: run.title, expectation: run.expectation }
          : {}),
      }).then((score) => settleRun(run.id, score));
    },
    [settleRun]
  );

  // A Test chat is ONE run, whichever reply it is on: created at the first
  // reply, and grown by every one after it. Each reply is scored on its own
  // as it arrives; the conversation is scored when it ends (`onEnded`).
  const onReply = useCallback(
    (reply: LiveReply, channel: RunChannel) => {
      const runId = chatRunId(reply.chatId);
      const focus = reply.transcript[reply.focusIndex];
      const scorable = !!focus && !focus.system;
      setRuns((prev) => {
        const existing = prev.find((r) => r.id === runId);
        // A chat's row reads as what the tester said last.
        const lastUser = latestUserText(reply.transcript);
        const pending: Record<string, RunScore> = scorable
          ? { [String(reply.focusIndex)]: { status: 'pending' } }
          : {};
        if (existing) {
          return prev.map((r) =>
            r.id === runId
              ? {
                  ...r,
                  title: lastUser || r.title,
                  transcript: reply.transcript,
                  endedAt: reply.endedAt,
                  turnScores: { ...(r.turnScores ?? {}), ...pending },
                }
              : r
          );
        }
        const run: ValidateRun = {
          id: runId,
          kind: 'test',
          title: lastUser,
          channel,
          versionLabel: versionLabelFor(channel.env),
          chatId: reply.chatId,
          transcript: reply.transcript,
          focusIndex: null,
          startedAt: reply.startedAt,
          endedAt: reply.endedAt,
          score: { status: 'idle' },
          turnScores: pending,
          // A Test chat is free-form; scenarios say what should handle them.
          expectedAgent: null,
        };
        return [run, ...prev];
      });
      if (!scorable) return;
      void scoreFocus({
        transcript: reply.transcript,
        focusIndex: reply.focusIndex,
        judge: scoringJudgeRef.current,
      }).then((score) => settleTurn(runId, reply.focusIndex, score));
    },
    [setRuns, settleTurn, versionLabelFor]
  );

  // Chats that ended, waiting for their runs to be in state to be scored
  // (see the effect below).
  const [endedChats, setEndedChats] = useState<string[]>([]);

  /* A "Call me" call or a widget chat (both read from the logs), saved as a
     Test run on its channel. */
  const [callChatId, setCallChatId] = useState<string | null>(null);
  const onCall = useCallback(
    (call: {
      chatId: string;
      transcript: ValidateTurn[];
      startedAt: number;
      done: boolean;
    }) => {
      if (!testChannel) return;
      const channel = testChannel;
      const runId = chatRunId(call.chatId);
      setCallChatId(call.chatId);
      setRuns((prev) => {
        const title = latestUserText(call.transcript);
        const existing = prev.find((r) => r.id === runId);
        if (existing) {
          return prev.map((r) =>
            r.id === runId
              ? {
                  ...r,
                  title: title || r.title,
                  transcript: call.transcript,
                  endedAt: Date.now(),
                }
              : r
          );
        }
        const run: ValidateRun = {
          id: runId,
          kind: 'test',
          title:
            title || (channel.voiceChannelId ? 'Phone call' : 'Widget chat'),
          channel,
          versionLabel: versionLabelFor(channel.env),
          chatId: call.chatId,
          transcript: call.transcript,
          focusIndex: null,
          startedAt: call.startedAt,
          endedAt: Date.now(),
          score: { status: 'idle' },
          turnScores: {},
          expectedAgent: null,
        };
        return [run, ...prev];
      });
      // Scored once, as a whole conversation, when the call and its logs are in.
      if (call.done) {
        setEndedChats((prev) =>
          prev.includes(call.chatId) ? prev : [...prev, call.chatId]
        );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setRuns, versionLabelFor, testChannel?.voiceChannelId, testChannel?.env]
  );

  // A chat that ended, waiting for its run to be in state to be scored. The
  // end can land in the same tick as the reply that created the run — the
  // bot's last reply is often what ends it — before React has committed it,
  // so scoring is picked up from here once `runs` has the run.
  const onEnded = useCallback(({ chatId }: LiveEnded) => {
    setEndedChats((prev) => (prev.includes(chatId) ? prev : [...prev, chatId]));
  }, []);
  useEffect(() => {
    if (!endedChats.length) return;
    const ready = endedChats
      .map((chatId) => runs.find((r) => r.id === chatRunId(chatId)))
      .filter((r): r is ValidateRun => !!r);
    if (!ready.length) return;
    setEndedChats((prev) =>
      prev.filter((chatId) => !ready.some((r) => r.chatId === chatId))
    );
    ready
      .filter((r) => r.score.status === 'idle')
      .forEach((r) => scoreConversation(r));
  }, [endedChats, runs, scoreConversation]);

  // Keyed on the picker identity: voice and its widget share an appId, and
  // switching between them is a new conversation.
  const testAppId = testChannel ? channelKey(testChannel) : null;
  useEffect(() => {
    if (!testChannel) {
      setChat(EMPTY_CHAT);
      return undefined;
    }
    const channel = testChannel;
    const instance = new LiveChat(channel, (reply) => onReply(reply, channel), {
      onEnded,
    });
    liveChat.current = instance;
    const unsubscribe = instance.subscribe(setChat);
    return () => {
      unsubscribe();
      instance.dispose();
      if (liveChat.current === instance) liveChat.current = null;
    };
    // A new chat per channel; `testChannel` itself is rebuilt every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testAppId, onReply, onEnded]);

  const callRun = callChatId
    ? runs.find((r) => r.id === chatRunId(callChatId))
    : undefined;

  const chatRun = chat.chatId
    ? runs.find((r) => r.id === chatRunId(chat.chatId as string))
    : undefined;

  /* Scenario. */
  const [scenarioError, setScenarioError] = useState<string | null>(null);
  const [openRun, setOpenRun] = useState<string | null>(null);
  const [scenario, setScenario] = useState<{
    step: ScenarioStep;
    transcript: ValidateTurn[];
    channel: RunChannel;
  } | null>(null);
  const scenarioAbort = useRef<AbortController | null>(null);
  useEffect(() => () => scenarioAbort.current?.abort(), []);

  const startScenario = useCallback(
    async (input: {
      channel: RunChannel;
      description: string;
      expectation?: string;
      expectedAgent: ExpectedAgent | null;
      startAt: StartPosition;
    }) => {
      scenarioAbort.current?.abort();
      const controller = new AbortController();
      scenarioAbort.current = controller;
      setScenario({ step: 'writing', transcript: [], channel: input.channel });
      try {
        const result = await runScenario(
          withJudge(input, scoringJudgeRef.current),
          {
            onStep: (step, transcript) => {
              if (!controller.signal.aborted) {
                setScenario({ step, transcript, channel: input.channel });
              }
            },
            signal: controller.signal,
          }
        );
        addRun({
          id: newId(),
          kind: 'scenario',
          title: input.description,
          expectation: input.expectation,
          expectedAgent: input.expectedAgent,
          startAt: input.startAt,
          channel: input.channel,
          versionLabel: versionLabelFor(input.channel.env),
          chatId: result.chatId,
          transcript: result.transcript,
          focusIndex: null,
          startedAt: result.startedAt,
          endedAt: result.endedAt,
          score: result.score,
          turnScores: result.turnScores,
          stoppedBecause: result.stoppedBecause,
        });
        setOpenRun(null);
        setPanel('test');
      } catch (err) {
        if (!controller.signal.aborted) {
          setScenarioError(err instanceof Error ? err.message : String(err));
        }
      } finally {
        if (scenarioAbort.current === controller) scenarioAbort.current = null;
        setScenario(null);
      }
    },
    [addRun, versionLabelFor]
  );

  /* Suites. */
  const [view, setView] = useState<View>('runs');
  const [editing, setEditing] = useState<{
    suite: ValidateSuite;
    isNew: boolean;
  } | null>(null);
  const [saveCaseRun, setSaveCaseRun] = useState<ValidateRun | null>(null);
  const [suiteProgress, setSuiteProgress] = useState<SuiteProgress | null>(
    null
  );
  const suiteAbort = useRef<AbortController | null>(null);
  useEffect(() => () => suiteAbort.current?.abort(), []);
  const latestRuns = useMemo(() => latestSuiteRuns(suiteRuns), [suiteRuns]);

  const openSuiteEditor = (suite: ValidateSuite, isNew: boolean) => {
    setEditing({ suite, isNew });
    setPanel('suite');
  };
  const saveSuite = (suite: ValidateSuite) => {
    const saved = { ...suite, updatedAt: Date.now() };
    setSuites((prev) =>
      prev.some((x) => x.id === saved.id)
        ? prev.map((x) => (x.id === saved.id ? saved : x))
        : [saved, ...prev]
    );
    setEditing(null);
    setPanel('test');
    setView('suites');
  };
  const saveAsCase = (
    run: ValidateRun,
    target: { suiteId: string } | { newSuiteName: string }
  ) => {
    const c = caseFromRun(newId(), run);
    if ('suiteId' in target) {
      const suite = suites.find((x) => x.id === target.suiteId);
      if (!suite) return;
      openSuiteEditor({ ...suite, cases: [...suite.cases, c] }, false);
    } else {
      openSuiteEditor(
        newSuite(newId(), Date.now(), {
          name: target.newSuiteName,
          channelAppId: run.channel.appId,
          cases: [c],
        }),
        true
      );
    }
    setSaveCaseRun(null);
  };

  const runSuite = async (suiteId: string) => {
    const suite = suites.find((x) => x.id === suiteId);
    const channel = channels.find((c) => c.appId === suite?.channelAppId);
    if (!suite || !channel || suiteAbort.current) return;
    const tasks = expandSuite(suite);
    if (!tasks.length) return;

    const controller = new AbortController();
    suiteAbort.current = controller;
    // Every case of this suite run is scored by the judge it started with,
    // however the judges are edited while it runs.
    const suiteJudge = scoringJudgeRef.current;
    const suiteRunId = newId();
    const record = (patch: Partial<SuiteRun>) =>
      setSuiteRuns((prev) =>
        prev.map((sr) => (sr.id === suiteRunId ? { ...sr, ...patch } : sr))
      );
    setSuiteRuns((prev) => [
      {
        id: suiteRunId,
        suiteId: suite.id,
        suiteName: suite.name,
        channelName: channel.name,
        startedAt: Date.now(),
        endedAt: null,
        status: 'running',
        total: tasks.length,
        runIds: [],
      },
      ...prev,
    ]);
    setSuiteProgress({
      suiteRunId,
      suiteId: suite.id,
      done: 0,
      total: tasks.length,
    });

    let done = 0;
    await runWithConcurrency(
      tasks,
      async (task) => {
        const result = await runScenario(
          withJudge(scenarioInputOf(task, suite, channel), suiteJudge),
          { signal: controller.signal }
        );
        const run = runOfSuiteTask({
          id: newId(),
          task,
          suite,
          suiteRunId,
          channel,
          versionLabel: versionLabelFor(channel.env),
          result,
        });
        addRun(run);
        setSuiteRuns((prev) =>
          prev.map((sr) =>
            sr.id === suiteRunId
              ? { ...sr, runIds: [...sr.runIds, run.id] }
              : sr
          )
        );
        return run;
      },
      {
        limit: suite.concurrency,
        signal: controller.signal,
        onSettled: () => {
          done += 1;
          setSuiteProgress((p) => (p ? { ...p, done } : p));
        },
      }
    );

    record({
      status: controller.signal.aborted ? 'stopped' : 'done',
      endedAt: Date.now(),
    });
    if (suiteAbort.current === controller) suiteAbort.current = null;
    setSuiteProgress(null);
  };

  /* Filters. */
  const [kind, setKind] = useState<KindFilter>('all');
  const [time, setTime] = useState<TimeFilter>('any');
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => setNow(Date.now()), [runs.length]);

  const inTime = useMemo(
    () =>
      filterByTime(
        who === 'mine' && shared.status === 'on'
          ? runs.filter((r) => !r.suite?.scheduleId && !shared.authorOf(r.id))
          : runs,
        time,
        now
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [runs, time, now, who, shared.status, shared.authorOf]
  );
  const counts = kindCounts(inTime);
  const shown = filterByKind(inTime, kind);

  /** From a suite's summary: show that run, open, in Runs. */
  const openRunInList = (runId: string) => {
    setView('runs');
    setKind('all');
    setTime('any');
    setOpenRun(runId);
    setTimeout(
      () =>
        document
          .getElementById(`validate-run-${runId}`)
          ?.scrollIntoView({ block: 'center', behavior: 'smooth' }),
      50
    );
  };

  return (
    <div className="c-validate-stage" test-id="bobcat-validate-stage">
      <div className="c-validate-stage__main">
        <div className="c-validate-stage__header">
          <div>
            <h1 className="c-validate-stage__title">Validate</h1>
            <p className="c-validate-stage__sub">
              Prove the solution works before you deploy it. Chat with it on any
              of its channels in Test, or simulate a scenario — every reply is
              timed, scored and traced here.
            </p>
          </div>
          <div className="c-validate-stage__actions">
            <Button
              size="small"
              variant="ghost"
              test-id="bobcat-validate-how-scored"
              onClick={() => setPanel(panel === 'scoring' ? 'test' : 'scoring')}
            >
              <Icon name="info" slot="prefix" size="xsmall" />
              How runs are scored
            </Button>
            <Button
              size="small"
              variant="primary"
              test-id="bobcat-validate-new-scenario"
              onClick={() => {
                setScenarioError(null);
                setPanel('scenario');
              }}
            >
              <Icon name="stars" slot="prefix" size="xsmall" />
              New scenario
            </Button>
          </div>
        </div>

        <BobcatTabGroup
          // Remounted on every view change: the group keeps its own idea of
          // the active tab, so a switch made from code (opening a suite's run
          // shows Runs) left it on the old tab, and clicking that tab again
          // fired nothing.
          key={view}
          variant="flush"
          className="c-validate-views"
          test-id="bobcat-validate-views"
          onTabShow={(e: CustomEvent<{ name: string }>) =>
            setView(
              e.detail.name === 'suites' || e.detail.name === 'judges'
                ? e.detail.name
                : 'runs'
            )
          }
        >
          <Tab
            slot="tab"
            panel="runs"
            active={view === 'runs'}
            test-id="bobcat-validate-view-runs"
          >
            <span className="c-validate-views__tab">
              Runs <span>{runs.length}</span>
            </span>
          </Tab>
          <Tab
            slot="tab"
            panel="suites"
            active={view === 'suites'}
            test-id="bobcat-validate-view-suites"
          >
            <span className="c-validate-views__tab">
              Suites <span>{suites.length}</span>
            </span>
          </Tab>
          <Tab
            slot="tab"
            panel="judges"
            active={view === 'judges'}
            test-id="bobcat-validate-view-judges"
          >
            <span className="c-validate-views__tab">
              Judges <span>{judges.length}</span>
            </span>
          </Tab>
        </BobcatTabGroup>

        {view === 'judges' ? (
          (() => {
            const judge = judges.find((j) => j.id === openJudge);
            return judge ? (
              <JudgeDetail
                judge={judge}
                runs={runs}
                makeId={newId}
                onBack={() => setOpenJudge(null)}
                onSave={saveJudge}
                onSamples={setJudgeSamples}
                onDelete={(id) => {
                  setJudges((prev) => prev.filter((j) => j.id !== id));
                  setOpenJudge(null);
                }}
              />
            ) : (
              <JudgesView
                judges={judges}
                onOpen={setOpenJudge}
                onNew={() => setPanel('judge-new')}
              />
            );
          })()
        ) : view === 'suites' ? (
          <SuitesView
            suites={suites}
            channels={channels}
            latestRuns={latestRuns}
            runs={runs}
            progress={suiteProgress}
            now={now}
            onNew={() =>
              openSuiteEditor(
                newSuite(newId(), Date.now(), {
                  channelAppId: testChannel?.appId ?? null,
                }),
                true
              )
            }
            onEdit={(id) => {
              const suite = suites.find((x) => x.id === id);
              if (suite) openSuiteEditor(suite, false);
            }}
            onDuplicate={(id) => {
              const suite = suites.find((x) => x.id === id);
              if (suite) {
                setSuites((prev) => [
                  duplicateSuite(suite, newId, Date.now()),
                  ...prev,
                ]);
              }
            }}
            onDelete={(id) => {
              setSuites((prev) => prev.filter((x) => x.id !== id));
              if (editing?.suite.id === id) {
                setEditing(null);
                setPanel('test');
              }
            }}
            onRun={(id) => void runSuite(id)}
            onStop={() => suiteAbort.current?.abort()}
            onOpenRun={openRunInList}
            schedules={shared.schedules}
            onSchedule={(id) => {
              setSchedulingSuite(id);
              setPanel('schedule');
            }}
            onImport={async (file) => {
              try {
                const text = await file.text();
                const name =
                  file.name.replace(/\.(json|csv)$/i, '') || 'Imported suite';
                let suite: ValidateSuite;
                if (/\.csv$/i.test(file.name)) {
                  const { cases } = casesFromCsv(text, newId);
                  suite = newSuite(newId(), Date.now(), { name, cases });
                } else {
                  ({ suite } = suiteFromJson(text, newId, {
                    now: Date.now(),
                    fallbackName: name,
                  }));
                }
                // Opened as a new suite: pick the channel, check, save.
                openSuiteEditor(
                  { ...suite, channelAppId: testChannel?.appId ?? null },
                  true
                );
                return null;
              } catch (err) {
                return err instanceof Error ? err.message : String(err);
              }
            }}
          />
        ) : (
          <>
            <div className="c-validate-filters">
              {shared.status === 'on' ? (
                <div
                  className="c-validate-segments"
                  role="radiogroup"
                  aria-label="Whose runs"
                  test-id="bobcat-validate-who"
                >
                  {(
                    [
                      ['everyone', 'Everyone'],
                      ['mine', 'Mine'],
                    ] as const
                  ).map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      role="radio"
                      aria-checked={who === value}
                      className={who === value ? 'is-active' : ''}
                      onClick={() => setWho(value)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              ) : (
                <span
                  className="c-validate-filters__who"
                  test-id="bobcat-validate-shared-status"
                  title={
                    shared.status === 'loading'
                      ? 'Loading the shared history…'
                      : `Runs are saved in this browser only${
                          shared.problem ? ` — ${shared.problem}` : ''
                        }.`
                  }
                >
                  <Icon name="users" size="xsmall" />{' '}
                  {shared.status === 'loading' ? 'Loading…' : 'This browser'}
                </span>
              )}
              <select
                className="c-validate-select"
                test-id="bobcat-validate-time"
                value={time}
                onChange={(e) => setTime(e.target.value as TimeFilter)}
                aria-label="Time range"
              >
                {TIME_FILTERS.map((f) => (
                  <option key={f.value} value={f.value}>
                    {f.label}
                  </option>
                ))}
              </select>
            </div>

            <Summary runs={inTime} />

            {scenarioError && (
              <p className="c-validate-error" role="alert">
                The scenario couldn’t run: {scenarioError}
              </p>
            )}

            <div className="c-validate-runs-bar">
              <div className="c-validate-tabs" role="tablist">
                {(
                  [
                    ['all', 'All'],
                    ['test', 'Tests'],
                    ['scenario', 'Scenarios'],
                    ['failed', 'Failed'],
                  ] as const
                ).map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    role="tab"
                    aria-selected={kind === value}
                    className={kind === value ? 'is-active' : ''}
                    test-id={`bobcat-validate-tab-${value}`}
                    onClick={() => setKind(value)}
                  >
                    {label} <span>{counts[value]}</span>
                  </button>
                ))}
              </div>
              <span className="c-validate-runs-bar__meta">
                {shown.length} {shown.length === 1 ? 'run' : 'runs'} · newest
                first
              </span>
            </div>

            <div className="c-validate-runs" test-id="bobcat-validate-runs">
              {shown.length === 0 ? (
                <div className="c-validate-runs__empty">
                  {runs.length === 0
                    ? 'No runs yet. Send a message in Test, or run a scenario — each one is scored and listed here.'
                    : 'No runs match these filters.'}
                </div>
              ) : (
                shown.map((run) => (
                  <RunRow
                    key={run.id}
                    run={run}
                    author={shared.authorOf(run.id)}
                    now={now}
                    open={openRun === run.id}
                    onToggle={() =>
                      setOpenRun(openRun === run.id ? null : run.id)
                    }
                    onScoreConversation={scoreConversation}
                    onSaveCase={(r) => {
                      setSaveCaseRun(r);
                      setPanel('save-case');
                    }}
                    judges={judges}
                    onJudgeResult={setRunJudgeResult}
                  />
                ))
              )}
            </div>
          </>
        )}
      </div>

      <aside className="c-validate-stage__panel">
        {panel === 'judge-new' ? (
          <NewJudgePanel
            makeId={newId}
            onCreate={(judge) => {
              setJudges((prev) => [...prev, judge]);
              setView('judges');
              setOpenJudge(judge.id);
              setPanel('test');
            }}
            onClose={() => setPanel('test')}
          />
        ) : panel === 'schedule' && schedulingSuite ? (
          (() => {
            const suite = suites.find((x) => x.id === schedulingSuite);
            if (!suite) return null;
            return (
              <SchedulePanel
                suite={suite}
                channel={
                  channels.find((c) => c.appId === suite.channelAppId) ?? null
                }
                existing={
                  shared.schedules.find((x) => x.suiteId === suite.id) ?? null
                }
                judge={scoringJudge}
                available={shared.status === 'on'}
                makeId={newId}
                onSave={shared.saveSchedule}
                onDelete={shared.deleteSchedule}
                onClose={() => {
                  setSchedulingSuite(null);
                  setPanel('test');
                }}
              />
            );
          })()
        ) : panel === 'suite' && editing ? (
          <SuitePanel
            suite={editing.suite}
            isNew={editing.isNew}
            channels={channels}
            agents={agents}
            makeId={newId}
            onSave={saveSuite}
            onClose={() => {
              setEditing(null);
              setPanel('test');
            }}
          />
        ) : panel === 'save-case' && saveCaseRun ? (
          <SaveCasePanel
            run={saveCaseRun}
            suites={suites}
            onSave={(target) => saveAsCase(saveCaseRun, target)}
            onClose={() => {
              setSaveCaseRun(null);
              setPanel('test');
            }}
          />
        ) : panel === 'scoring' ? (
          <ScoringPanel
            criteria={asRubricCriteria(rubricCriteriaOf(rubricJudge))}
            edited={
              rubricJudge.kind === 'rubric' && !!rubricJudge.criteria.length
            }
            onClose={() => setPanel('test')}
          />
        ) : panel === 'scenario' ? (
          <ScenarioPanel
            channels={channels}
            defaultChannel={testChannel}
            running={scenario}
            agents={agents}
            onRun={startScenario}
            onStop={() => scenarioAbort.current?.abort()}
            onClose={() => setPanel('test')}
          />
        ) : (
          <TestPanel
            channels={testChannels}
            voiceLine={
              testChannel?.voiceChannelId
                ? (voiceLines.get(testChannel.voiceChannelId) ?? null)
                : null
            }
            nodeMessages={nodeMessages}
            onCall={onCall}
            callScore={callRun?.score}
            onScoreCall={callRun ? () => scoreConversation(callRun) : undefined}
            channel={testChannel}
            onChannel={setChosenAppId}
            versionLabel={testChannel ? versionLabelFor(testChannel.env) : null}
            noChannelReason={describeNoTestChannel(rawChannels)}
            chat={chat}
            onSend={(text) => void liveChat.current?.send(text)}
            onPick={(option) => void liveChat.current?.pick(option)}
            webviewHandlers={webviewHandlers}
            onRestart={() => void liveChat.current?.reset()}
            onEnd={() => void liveChat.current?.end()}
            onStart={() => void liveChat.current?.start()}
            turnScores={chatRun?.turnScores}
            conversationScore={chatRun?.score}
            onScoreConversation={
              chatRun ? () => scoreConversation(chatRun) : undefined
            }
            widgetEnabled={pypeNextEnabled}
          />
        )}
      </aside>
    </div>
  );
});
